import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const skillGroups = [
  {
    category: 'Languages',
    color: 'primary',
    skills: ['Python', 'Java', 'JavaScript', 'TypeScript', 'C', 'C++', 'PHP', 'Bash'],
  },
  {
    category: 'Frontend & Mobile',
    color: 'accent',
    skills: ['React.js', 'React Native', 'Flutter', 'HTML/CSS', 'Bootstrap', 'Redux'],
  },
  {
    category: 'Backend & APIs',
    color: 'blue',
    skills: ['Node.js', 'Flask', 'Django', '.NET REST APIs', 'RESTful APIs', 'Microservices', 'MVC'],
  },
  {
    category: 'AI / ML',
    color: 'violet',
    skills: ['TensorFlow', 'PyTorch', 'Generative AI', 'Predictive Analytics', 'Computer Vision', 'ML Algorithms'],
  },
  {
    category: 'Databases',
    color: 'orange',
    skills: ['MySQL', 'MongoDB', 'PostgreSQL', 'SQLite', 'Firebase'],
  },
  {
    category: 'Cloud & DevOps',
    color: 'sky',
    skills: ['AWS S3', 'AWS EC2', 'AWS RDS', 'Microsoft Azure', 'Firebase', 'CI/CD Pipelines'],
  },
  {
    category: 'Tools & Platforms',
    color: 'rose',
    skills: ['Git', 'VS Code', 'Android Studio', 'Eclipse', 'NetBeans', 'XAMPP', 'Linux', 'Google Analytics'],
  },
];

const colorMap = {
  primary: 'bg-primary-50 text-primary-700 border-primary-100 hover:bg-primary-100',
  accent: 'bg-accent-50 text-accent-700 border-accent-100 hover:bg-accent-100',
  blue: 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100',
  violet: 'bg-violet-50 text-violet-700 border-violet-100 hover:bg-violet-100',
  orange: 'bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-100',
  sky: 'bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-100',
  rose: 'bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-100',
};

const headerColorMap = {
  primary: 'text-primary-600',
  accent: 'text-accent-600',
  blue: 'text-blue-600',
  violet: 'text-violet-600',
  orange: 'text-orange-600',
  sky: 'text-sky-600',
  rose: 'text-rose-600',
};

const dotColorMap = {
  primary: 'bg-primary-500',
  accent: 'bg-accent-500',
  blue: 'bg-blue-500',
  violet: 'bg-violet-500',
  orange: 'bg-orange-500',
  sky: 'bg-sky-500',
  rose: 'bg-rose-500',
};

function SkillGroup({ group, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 25 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay }}
      className="card p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className={`w-2.5 h-2.5 rounded-full ${dotColorMap[group.color]}`} />
        <h3 className={`font-semibold text-sm ${headerColorMap[group.color]}`}>
          {group.category}
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {group.skills.map((skill) => (
          <span
            key={skill}
            className={`tag border ${colorMap[group.color]} transition-colors cursor-default`}
          >
            {skill}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export default function Skills() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="skills" className="bg-slate-50">
      <div className="section-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-1 h-8 bg-gradient-to-b from-primary-600 to-accent-500 rounded-full" />
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Technical Skills</p>
          </div>
          <h2 className="section-title">My Tech Stack</h2>
          <p className="text-slate-400 text-sm mb-12">Tools and technologies I use to build real-world products.</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {skillGroups.map((group, i) => (
            <SkillGroup key={group.category} group={group} delay={i * 0.07} />
          ))}
        </div>

        {/* Specializations Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 card p-6 bg-gradient-to-r from-primary-50 via-blue-50 to-accent-50 border-primary-100"
        >
          <p className="text-sm font-semibold text-slate-600 mb-3">Specializations</p>
          <div className="flex flex-wrap gap-2">
            {[
              'Full Stack Development',
              'Cybersecurity',
              'EdTech Solutions',
              'System Design',
              'SEO Optimization',
              'Mobile Development',
              'Enterprise Software',
            ].map((spec) => (
              <span
                key={spec}
                className="bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm"
              >
                {spec}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
